import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, 
  Calendar,
  Clock,
  CheckCircle,
  Circle,
  Upload,
  FileText,
  Video,
  BookOpen,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Award,
  Play,
  Download,
  Lock
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { toast } from 'sonner';
import { API_BASE } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

const API = `${API_BASE}/api`;

const ACTIVITY_ICONS = {
  course: BookOpen,
  learning_lab: BookOpen,
  video: Video,
  masterclass: Video,
  aicc: ExternalLink,
  document: FileText,
  assignment_brief: Upload
};

export default function ProgrammeDetailPage() {
  const navigate = useNavigate();
  const { programmeId } = useParams();
  const { token, isAuthenticated, user } = useAuth();
  const fileInputRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const [programme, setProgramme] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedPeriods, setExpandedPeriods] = useState({});
  const [uploadingActivity, setUploadingActivity] = useState(null);

  useEffect(() => {
    fetchProgramme();
  }, [programmeId]);

  const fetchProgramme = async () => {
    try {
      const [progRes, progressRes] = await Promise.all([
        axios.get(`${API}/programmes/${programmeId}`, { headers }),
        isAuthenticated 
          ? axios.get(`${API}/programmes/${programmeId}/my-progress`, { headers }).catch(() => ({ data: null }))
          : Promise.resolve({ data: null })
      ]);
      
      setProgramme(progRes.data);
      setProgress(progressRes.data);
      
      // Expand first period by default
      if (progRes.data.periods?.length > 0) {
        setExpandedPeriods({ [progRes.data.periods[0].period_id]: true });
      }
    } catch (error) {
      toast.error('Failed to load programme');
      navigate('/programmes');
    } finally {
      setLoading(false);
    }
  };

  const togglePeriod = (periodId) => {
    setExpandedPeriods(prev => ({ ...prev, [periodId]: !prev[periodId] }));
  };

  const isActivityComplete = (activityId) => {
    return progress?.completed_activity_ids?.includes(activityId);
  };

  const getPoeSubmission = (activityId) => {
    return progress?.poe_submissions?.find(s => s.activity_id === activityId);
  };

  const handleMarkComplete = async (activityId) => {
    try {
      await axios.post(`${API}/programmes/${programmeId}/activities/${activityId}/complete`, {}, { headers });
      toast.success('Activity marked complete!');
      fetchProgramme();
    } catch (error) {
      toast.error('Failed to mark activity complete');
    }
  };

  const handlePoeUpload = async (activityId, file) => {
    if (!file) return;

    const allowedTypes = ['.pdf', '.ppt', '.pptx'];
    const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!allowedTypes.includes(fileExt)) {
      toast.error('Only PDF and PowerPoint files are allowed');
      return;
    }

    setUploadingActivity(activityId);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(
        `${API}/programmes/${programmeId}/activities/${activityId}/submit-poe`,
        formData,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Portfolio of Evidence submitted!');
      fetchProgramme();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit';
      toast.error(message);
    } finally {
      setUploadingActivity(null);
    }
  };

  const launchActivity = async (activity) => {
    switch (activity.activity_type) {
      case 'course':
        if (activity.content_id) {
          navigate(`/courses/${activity.content_id}`);
        }
        break;
      case 'learning_lab':
        if (activity.content_id) {
          navigate(`/labs/${activity.content_id}`);
        }
        break;
      case 'masterclass':
        if (activity.content_id) {
          navigate(`/masterclasses/${activity.content_id}`);
        }
        break;
      case 'aicc':
      case 'video':
        // Open iframe content in a modal or new tab
        if (activity.external_url) {
          window.open(activity.external_url, '_blank');
        }
        break;
      case 'document':
      case 'assignment_brief':
        // Download document from the programme workflow endpoint
        if (activity.document_id || activity.document_filename) {
          try {
            const response = await axios.get(
              `${API}/programmes/${programmeId}/activities/${activity.activity_id}/download-document`,
              { headers, responseType: 'blob' }
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', activity.document_filename || 'document.pdf');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Document downloaded');
          } catch (error) {
            console.error('Download error:', error);
            toast.error('Failed to download document');
          }
        } else if (activity.document_url) {
          window.open(`${API_BASE}${activity.document_url}`, '_blank');
        }
        break;
      default:
        break;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-zinc-800 rounded w-1/3" />
            <div className="h-64 bg-zinc-800 rounded" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!programme) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-4 py-8 text-center">
          <p className="text-zinc-400">Programme not found</p>
        </div>
      </Layout>
    );
  }

  const isEnrolled = progress?.enrollment !== undefined;
  const overallProgress = progress?.enrollment?.overall_progress || 0;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => navigate('/programmes')}
          className="text-zinc-400 hover:text-white mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Programmes
        </Button>

        {/* Programme Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between">
            <div>
              <Badge 
                variant="outline" 
                className="border-teal-500 text-teal-400 mb-3"
              >
                {programme.structure_type === 'weeks' ? 'Weekly' : 'Monthly'} Programme
              </Badge>
              <h1 className="text-3xl font-bold text-white mb-2">
                {programme.programme_name || 'Professional Programme'}
              </h1>
              {programme.description && (
                <p className="text-zinc-400 max-w-2xl">{programme.description}</p>
              )}
            </div>
            
            {isEnrolled && (
              <div className="text-right">
                <div className="text-sm text-zinc-400 mb-1">Your Progress</div>
                <div className="text-3xl font-bold text-teal-400">{overallProgress}%</div>
              </div>
            )}
          </div>

          {/* Programme Info Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            {programme.professional_body && (
              <div className="p-4 bg-zinc-900 rounded-lg border border-zinc-800">
                <p className="text-xs text-zinc-500 uppercase tracking-wide">Professional Body</p>
                <p className="text-white font-medium mt-1">{programme.professional_body}</p>
              </div>
            )}
            {programme.nqf_level && (
              <div className="p-4 bg-zinc-900 rounded-lg border border-zinc-800">
                <p className="text-xs text-zinc-500 uppercase tracking-wide">NQF Level</p>
                <p className="text-white font-medium mt-1">{programme.nqf_level}</p>
              </div>
            )}
            {programme.duration && (
              <div className="p-4 bg-zinc-900 rounded-lg border border-zinc-800">
                <p className="text-xs text-zinc-500 uppercase tracking-wide">Duration</p>
                <p className="text-white font-medium mt-1">{programme.duration}</p>
              </div>
            )}
            {programme.credits && (
              <div className="p-4 bg-zinc-900 rounded-lg border border-zinc-800">
                <p className="text-xs text-zinc-500 uppercase tracking-wide">Credits</p>
                <p className="text-white font-medium mt-1">{programme.credits}</p>
              </div>
            )}
          </div>

          {/* Progress Bar for Enrolled Users */}
          {isEnrolled && (
            <div className="mt-6 p-4 bg-zinc-900 rounded-lg border border-zinc-800">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-zinc-400">Overall Progress</span>
                <span className="text-sm text-teal-400 font-medium">
                  {progress.enrollment.completed_activities} / {progress.enrollment.total_activities} activities
                </span>
              </div>
              <Progress value={overallProgress} className="h-2 bg-zinc-800" />
            </div>
          )}
        </div>

        {/* Not Enrolled Message */}
        {isAuthenticated && !isEnrolled && (
          <Card className="bg-amber-500/10 border-amber-500/30 mb-8">
            <CardContent className="p-4 flex items-center gap-3">
              <Lock className="h-5 w-5 text-amber-400" />
              <p className="text-amber-200">
                You are not enrolled in this programme. Contact your administrator to request enrollment.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Programme Structure */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-white mb-4">
            Programme Structure
          </h2>

          {programme.periods?.length === 0 ? (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="py-12 text-center">
                <Calendar className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">No content has been added to this programme yet.</p>
              </CardContent>
            </Card>
          ) : (
            programme.periods?.map((period, periodIndex) => {
              const periodActivities = period.activities || [];
              const completedInPeriod = periodActivities.filter(a => isActivityComplete(a.activity_id)).length;
              const periodProgress = periodActivities.length > 0 
                ? Math.round((completedInPeriod / periodActivities.length) * 100) 
                : 0;

              return (
                <Card key={period.period_id} className="bg-zinc-900 border-zinc-800">
                  <Collapsible
                    open={expandedPeriods[period.period_id]}
                    onOpenChange={() => togglePeriod(period.period_id)}
                  >
                    <CollapsibleTrigger asChild>
                      <CardHeader className="cursor-pointer hover:bg-zinc-800/50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              periodProgress === 100 
                                ? 'bg-teal-500/20 text-teal-400' 
                                : 'bg-zinc-800 text-zinc-400'
                            }`}>
                              {periodProgress === 100 ? (
                                <CheckCircle className="h-5 w-5" />
                              ) : (
                                <span className="font-bold">{periodIndex + 1}</span>
                              )}
                            </div>
                            <div>
                              <CardTitle className="text-white text-lg">
                                {period.title}
                              </CardTitle>
                              <p className="text-sm text-zinc-400">
                                {completedInPeriod} / {periodActivities.length} activities completed
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            {isEnrolled && (
                              <div className="text-right">
                                <div className="text-sm font-medium text-teal-400">{periodProgress}%</div>
                              </div>
                            )}
                            {expandedPeriods[period.period_id] ? (
                              <ChevronUp className="h-5 w-5 text-zinc-400" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-zinc-400" />
                            )}
                          </div>
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <CardContent className="pt-0">
                        {period.description && (
                          <p className="text-zinc-400 text-sm mb-4 pl-14">{period.description}</p>
                        )}
                        
                        <div className="space-y-3 pl-14">
                          {periodActivities.map((activity, activityIndex) => {
                            const Icon = ACTIVITY_ICONS[activity.activity_type] || FileText;
                            const isComplete = isActivityComplete(activity.activity_id);
                            const poeSubmission = getPoeSubmission(activity.activity_id);
                            const isUploading = uploadingActivity === activity.activity_id;

                            return (
                              <div 
                                key={activity.activity_id}
                                className={`p-4 rounded-lg border ${
                                  isComplete 
                                    ? 'bg-teal-500/5 border-teal-500/30' 
                                    : 'bg-zinc-800/50 border-zinc-700'
                                }`}
                              >
                                <div className="flex items-start gap-4">
                                  <div className={`p-2 rounded-lg ${
                                    isComplete ? 'bg-teal-500/20' : 'bg-zinc-700'
                                  }`}>
                                    <Icon className={`h-5 w-5 ${
                                      isComplete ? 'text-teal-400' : 'text-zinc-400'
                                    }`} />
                                  </div>
                                  
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <h4 className="text-white font-medium">{activity.title}</h4>
                                      <Badge variant="outline" className="text-xs border-zinc-600 text-zinc-400">
                                        {activity.activity_type.replace('_', ' ')}
                                      </Badge>
                                      {isComplete && (
                                        <Badge className="bg-teal-500/20 text-teal-400 text-xs">
                                          <CheckCircle className="h-3 w-3 mr-1" />
                                          Complete
                                        </Badge>
                                      )}
                                    </div>
                                    
                                    {activity.description && (
                                      <p className="text-sm text-zinc-400 mb-3">{activity.description}</p>
                                    )}

                                    {/* Action Buttons */}
                                    <div className="flex items-center gap-2 flex-wrap">
                                      {/* Launch/Download Button */}
                                      {(activity.content_id || activity.external_url || activity.document_url || activity.document_id || activity.document_filename) && (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => launchActivity(activity)}
                                          className="border-zinc-600 text-zinc-300 hover:bg-zinc-700"
                                        >
                                          {activity.activity_type === 'document' || activity.activity_type === 'assignment_brief' ? (
                                            <>
                                              <Download className="h-4 w-4 mr-1" />
                                              Download {activity.activity_type === 'assignment_brief' ? 'Brief' : 'Document'}
                                            </>
                                          ) : activity.activity_type === 'video' || activity.activity_type === 'aicc' || activity.activity_type === 'masterclass' ? (
                                            <>
                                              <ExternalLink className="h-4 w-4 mr-1" />
                                              Open in New Tab
                                            </>
                                          ) : (
                                            <>
                                              <Play className="h-4 w-4 mr-1" />
                                              Launch
                                            </>
                                          )}
                                        </Button>
                                      )}

                                      {/* Mark Complete (for non-POE activities) */}
                                      {isEnrolled && !activity.allows_poe_submission && !isComplete && (
                                        <Button
                                          size="sm"
                                          onClick={() => handleMarkComplete(activity.activity_id)}
                                          className="bg-teal-600 hover:bg-teal-700 text-white"
                                        >
                                          <CheckCircle className="h-4 w-4 mr-1" />
                                          Mark Complete
                                        </Button>
                                      )}

                                      {/* POE Submission */}
                                      {isEnrolled && activity.allows_poe_submission && (
                                        <>
                                          {poeSubmission ? (
                                            <div className="flex items-center gap-2">
                                              <Badge className={
                                                poeSubmission.status === 'graded'
                                                  ? 'bg-teal-500/20 text-teal-400'
                                                  : 'bg-amber-500/20 text-amber-400'
                                              }>
                                                {poeSubmission.status === 'graded' ? (
                                                  <>Grade: {poeSubmission.grade_percentage}%</>
                                                ) : (
                                                  <>Submitted - Pending Grade</>
                                                )}
                                              </Badge>
                                              {poeSubmission.feedback && (
                                                <span className="text-xs text-zinc-400">
                                                  Feedback: {poeSubmission.feedback}
                                                </span>
                                              )}
                                            </div>
                                          ) : (
                                            <>
                                              <input
                                                type="file"
                                                accept=".pdf,.ppt,.pptx"
                                                className="hidden"
                                                id={`poe-${activity.activity_id}`}
                                                onChange={(e) => handlePoeUpload(activity.activity_id, e.target.files[0])}
                                              />
                                              <Button
                                                size="sm"
                                                onClick={() => document.getElementById(`poe-${activity.activity_id}`).click()}
                                                disabled={isUploading}
                                                className="bg-amber-600 hover:bg-amber-700 text-white"
                                              >
                                                <Upload className="h-4 w-4 mr-1" />
                                                {isUploading ? 'Uploading...' : 'Submit POE'}
                                              </Button>
                                              <span className="text-xs text-zinc-500">PDF or PPT only</span>
                                            </>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </CollapsibleContent>
                  </Collapsible>
                </Card>
              );
            })
          )}
        </div>

        {/* Additional Programme Info */}
        {(programme.facilitator_name || programme.moderator_name || programme.start_date || programme.end_date) && (
          <Card className="bg-zinc-900 border-zinc-800 mt-8">
            <CardHeader>
              <CardTitle className="text-white text-lg">Programme Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {programme.facilitator_name && (
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wide">Facilitator</p>
                    <p className="text-white mt-1">{programme.facilitator_name}</p>
                  </div>
                )}
                {programme.moderator_name && (
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wide">Moderator</p>
                    <p className="text-white mt-1">{programme.moderator_name}</p>
                  </div>
                )}
                {programme.start_date && (
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wide">Start Date</p>
                    <p className="text-white mt-1">{formatDate(programme.start_date)}</p>
                  </div>
                )}
                {programme.end_date && (
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wide">End Date</p>
                    <p className="text-white mt-1">{formatDate(programme.end_date)}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
